// ルート → 必要な DVI 資産 → フォントの導出 (的を絞った先読み用)。
//
// 各ページから遷移可能なページは articles.json から決定的に分かるため、
// 「現在ページ + 遷移先候補」が使う DVI をここで列挙し、ビルド時生成の
// fonts-usage.json (DVI → 使用フォントファイル) と突き合わせて、必要な
// フォントだけを先読みする。純関数の集まりで、取得は warmup.ts が行う。

import type { Article, ArticlesIndex, SolutionWork } from '../types/articles';

/** 作品の問題グループ (巻ごと + 単巻直下)。base は DVI/URL の共通接頭辞。 */
function workGroups(work: SolutionWork) {
  return [
    ...work.books.map((b) => ({
      base: `solutions/${work.key}/${b.key}`,
      problems: b.problems,
    })),
    { base: `solutions/${work.key}`, problems: work.problems },
  ];
}

function articleDvis(article: Article): string[] {
  const out: string[] = [];
  if (article.metaPages) {
    out.push(`articles/${article.slug}/meta.formulas.dvi`);
  }
  for (const stem of article.fragments ?? []) {
    out.push(`articles/${article.slug}/${stem}.formulas.dvi`);
  }
  const link = article.link;
  if (link && link.url.startsWith('/') && /\.dvi$/i.test(link.url)) {
    out.push(link.url.slice(1));
  }
  return out;
}

/** ルートが自身の表示に使う DVI 資産 (webroot/public 相対パス)。 */
export function dviPathsForRoute(index: ArticlesIndex, path: string): string[] {
  if (path === '/' && index.site.metaPages) {
    return ['site/meta.formulas.dvi'];
  }
  const seg = path.split('/').filter(Boolean);
  // 子要素トップ (/math/ring-theory 等): 記事カードのメタ数式
  if (seg.length === 2) {
    for (const category of index.categories) {
      if (category.key !== seg[0]) {
        continue;
      }
      const child = category.children.find((c) => c.key === seg[1]);
      if (!child) {
        continue;
      }
      const out = child.articles.flatMap(articleDvis);
      if (child.kind === 'solutions') {
        for (const work of index.solutions) {
          for (const { base, problems } of workGroups(work)) {
            for (const problem of problems) {
              if (problem.metaPages) {
                out.push(`${base}/${problem.path}/meta.formulas.dvi`);
              }
            }
          }
        }
      }
      return out;
    }
  }
  // 記事 (/math/:slug, /logic/:slug)
  if (seg.length === 2) {
    for (const category of index.categories) {
      for (const child of category.children) {
        const article = child.articles.find((a) => a.section === seg[0] && a.slug === seg[1]);
        if (article) {
          return articleDvis(article);
        }
      }
    }
  }
  // 解答集の問題 (/solutions/<work>/(<book>/)<パス>)
  if (seg[0] === 'solutions' && seg.length >= 3) {
    const work = index.solutions.find((w) => w.key === seg[1]);
    if (work) {
      const route = seg.join('/');
      for (const { base, problems } of workGroups(work)) {
        const problem = problems.find((p) => `${base}/${p.path}` === route);
        if (problem) {
          const dir = `${base}/${problem.path}`;
          const out = problem.fragments.map((stem) => `${dir}/${stem}.formulas.dvi`);
          if (problem.metaPages) {
            out.push(`${dir}/meta.formulas.dvi`);
          }
          return out;
        }
      }
    }
  }
  return [];
}

/** ルートからリンクで遷移可能なルート (ナビ・一覧・前後リンクの写し)。 */
export function neighborRoutesFor(index: ArticlesIndex, path: string): string[] {
  const seg = path.split('/').filter(Boolean);
  const childRoutes = (categoryKey: string) => {
    const category = index.categories.find((c) => c.key === categoryKey);
    return category ? category.children.map((c) => `/${category.key}/${c.key}`) : [];
  };
  // ホーム・カテゴリトップ: 子要素トップの一覧
  if (path === '/' || seg.length === 1) {
    return index.categories.flatMap((c) => c.children.map((ch) => `/${c.key}/${ch.key}`));
  }
  if (seg.length === 2) {
    for (const category of index.categories) {
      const child = category.children.find((c) => category.key === seg[0] && c.key === seg[1]);
      if (child) {
        // 子要素トップ: 記事ページ + 同カテゴリの他の子要素 (+ 解答集の作品)
        const out = child.articles.map((a) => `/${a.section}/${a.slug}`);
        out.push(...childRoutes(category.key));
        if (child.kind === 'solutions') {
          out.push(...index.solutions.map((w) => `/solutions/${w.key}`));
        }
        return out;
      }
      for (const c of category.children) {
        if (c.articles.some((a) => a.section === seg[0] && a.slug === seg[1])) {
          // 記事ページ: 所属子要素のトップへ戻る導線
          return [`/${category.key}/${c.key}`];
        }
      }
    }
  }
  if (seg[0] === 'solutions' && seg.length >= 2) {
    const work = index.solutions.find((w) => w.key === seg[1]);
    if (work) {
      if (seg.length === 2) {
        // 作品トップ: 全問題ページ + 他の作品
        const out = workGroups(work).flatMap(({ base, problems }) =>
          problems.map((p) => `/${base}/${p.path}`),
        );
        out.push(...index.solutions.map((w) => `/solutions/${w.key}`));
        return out;
      }
      // 問題ページ: 同じ巻の他の問題 + 作品トップ
      const out = workGroups(work).flatMap(({ base, problems }) =>
        problems.map((p) => `/${base}/${p.path}`),
      );
      out.push(`/solutions/${work.key}`);
      return out;
    }
  }
  return [];
}

/** 現在ページと遷移先候補が必要とするフォントファイル名の集合。 */
export function fontFilesForRoute(
  index: ArticlesIndex,
  usage: Record<string, string[]>,
  path: string,
): string[] {
  const routes = [path, ...neighborRoutesFor(index, path)];
  const files = new Set<string>();
  for (const route of routes) {
    for (const dvi of dviPathsForRoute(index, route)) {
      for (const file of usage[dvi] ?? []) {
        files.add(file);
      }
    }
  }
  return [...files].sort();
}
